import { useAuth } from "../../../context";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { SupplierQuestionnaire } from "../../../api";
import { useToast } from "@chakra-ui/react";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { t } from "i18next";

export const supplierQuestionnaireKeys = {
  all: () => ["supplierQuestionnaire"],
  active: () => [...supplierQuestionnaireKeys.all(), "active"],
  owned: () => [...supplierQuestionnaireKeys.all(), "owned"],
  structure: () => ["supplierQuestionnaireStructure"],
  supplierQuestionnaireById: () => ["supplierQuestionnaireById"],
  scopeOneConsumptionTypes: () => ["scopeOneConsumptionTypes"],
  scopeTwoConsumptionTypes: () => ["scopeTwoConsumptionTypes"]
};

export function useGetAllSupplierQuestionnaires(queryParams, config) {
  const { callApi } = useAuth();
  return useQuery([...supplierQuestionnaireKeys.all(), queryParams],
    () => callApi(SupplierQuestionnaire.getAll, queryParams), config);
}

export function useCreateSupplierQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(data => callApi(SupplierQuestionnaire.create, data), config);
}

export function useBulkCreateSupplierQuestionnaires(config) {
  const { callApi } = useAuth();
  return useMutation(data => callApi(SupplierQuestionnaire.bulkCreate, data), config);
}

export function useUpdateSupplierQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, data }) => callApi(SupplierQuestionnaire.update, { id, data }), config);
}

export function useDeleteSupplierQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(recordId => callApi(SupplierQuestionnaire.delete, recordId), config);
}

export function useGetOwnedSupplierQuestionnaires(paramsString, config) {
  const { callApi } = useAuth();
  return useQuery([...supplierQuestionnaireKeys.owned(), paramsString],
    () => callApi(SupplierQuestionnaire.getOwnedSupplierQuestionnaires, paramsString), config);
}

export function useGetSupplierQuestionnaireById(id, config) {
  const { callApi } = useAuth();
  return useQuery([...supplierQuestionnaireKeys.supplierQuestionnaireById(), id],
    () => callApi(SupplierQuestionnaire.getSupplierQuestionnaireById, id), config);
}

export function useDefaultSupplierQuestionnaireConfig() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toast = useToast();

  return {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("supplierQuestionnaire.page.finalized"));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.active());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };
}

export function useFinalizeSupplierQuestionnaire(customConfig) {
  let config = useDefaultSupplierQuestionnaireConfig();
  if (customConfig) {
    config = customConfig;
  }
  const { callApi } = useAuth();
  return useMutation(id => callApi(SupplierQuestionnaire.finalizeQuestionnaire, id), config);
}

export function useGetSupplierQuestionnaireStructure(config) {
  const { callApi } = useAuth();
  return useQuery(supplierQuestionnaireKeys.structure(),
    () => callApi(SupplierQuestionnaire.getSupplierQuestionnaireStructure), config);
}

export function useSetQuestionnaireCategory(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, category }) => callApi(SupplierQuestionnaire.setQuestionnaireCategory, {
    id,
    category
  }), config);
}

export function useGetScopeOneConsumptionTypes(config) {
  const { callApi } = useAuth();
  return useQuery(supplierQuestionnaireKeys.scopeOneConsumptionTypes(),
    () => callApi(SupplierQuestionnaire.getScopeOneConsumptionTypes), config);
}

export function useGetScopeTwoConsumptionTypes(config) {
  const { callApi } = useAuth();
  return useQuery(supplierQuestionnaireKeys.scopeTwoConsumptionTypes(),
    () => callApi(SupplierQuestionnaire.getScopeTwoConsumptionTypes), config);
}

export function useUnfinaliseSupplierQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(({ questionnaireId, type }) =>
    callApi(SupplierQuestionnaire.unfinaliseSupplierQuestionnaire, { questionnaireId, type }), config);
}

export function useUpdateFinancialYearEndMonth(config) {
  const { callApi } = useAuth();
  return useMutation(({ questionnaireId, body }) =>
    callApi(SupplierQuestionnaire.updateFinancialYearEndMonth, { questionnaireId, body }), config);
}

export function useRevertQuestionnaireCategory(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, category }) => callApi(SupplierQuestionnaire.revertQuestionnaireCategory, {
    id,
    category
  }), config);
}